import { useId, useState, type ChangeEvent, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Label,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Spinner,
  Textarea,
  Toggle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { CheckIcon } from 'lucide-react';
import {
  boardTypeOf,
  isTrackerHue,
  isTrackerIcon,
  TRACKER_HUE_CLASSES,
  TRACKER_HUE_LABEL_KEYS,
  TRACKER_HUES,
  TRACKER_ICON_COMPONENTS,
  TRACKER_ICON_LABEL_KEYS,
  TRACKER_ICONS,
  trackerHueClasses,
  TrackerIconTile,
  useUpdateTrackerMutation,
  type Tracker,
  type TrackerHue,
  type TrackerIcon,
} from '@/entities/tracker';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import { track } from '@/shared/analytics';
import { guardNotistackDismissal } from '@/shared/ui/FormDialog/formShell.utils';

function EditTrackerForm({ tracker, onClose }: { tracker: Tracker; onClose: () => void }) {
  const { t } = useTranslation('trackers');
  const uid = useId();
  const workspaceId = useCurrentWorkspaceId() ?? '';
  const updateTracker = useUpdateTrackerMutation({ workspaceId });

  // Seeded once per opening and per tracker: the sheet's content mounts on open and unmounts on
  // close, which is what makes Cancel, Escape and the overlay discard whatever was typed.
  const [name, setName] = useState(tracker.name);
  const [description, setDescription] = useState(tracker.description ?? '');
  const [icon, setIcon] = useState<TrackerIcon>(
    isTrackerIcon(tracker.icon) ? tracker.icon : 'list-checks',
  );
  const [hue, setHue] = useState<TrackerHue>(isTrackerHue(tracker.hue) ? tracker.hue : 'neutral');

  const trimmedName = name.trim();
  const colours = trackerHueClasses(hue);
  const colorLabelId = `${uid}-color`;
  const iconLabelId = `${uid}-icon`;

  const save = () => {
    if (!trimmedName) return;
    updateTracker.mutate(
      {
        trackerId: tracker.id,
        body: { name: trimmedName, description: description.trim() || null, icon, hue },
      },
      {
        onSuccess: () => {
          onClose();
          if (trimmedName === tracker.name) return;
          track('tracker_renamed', {
            tracker_id: tracker.id,
            board_type: boardTypeOf(tracker.itemSource),
          });
        },
        onError: () =>
          notificationService.showError(t('trackers.editTrackerSheet.couldNotUpdateTheTracker')),
      },
    );
  };

  return (
    <TooltipProvider>
      <SheetHeader className="p-0 px-xl">
        <SheetTitle>{t('trackers.editTrackerSheet.editTracker')}</SheetTitle>
      </SheetHeader>

      {/* Spans the unpadded panel and holds the inset inside its own clip, so an edge control
          keeps its focus ring and the scrollbar sits at the panel's edge. */}
      <div className="flex min-h-0 flex-1 flex-col gap-lg overflow-y-auto px-xl py-2xs">
        <div className="flex flex-col gap-xs">
          <Label htmlFor={`${uid}-name`}>{t('trackers.editTrackerSheet.name')}</Label>
          <Input
            id={`${uid}-name`}
            size="lg"
            required
            value={name}
            maxLength={120}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-xs">
          <Label htmlFor={`${uid}-description`}>
            {t('trackers.editTrackerSheet.description')}
            <span className="font-normal text-muted-foreground">
              {t('trackers.editTrackerSheet.optional')}
            </span>
          </Label>
          <Textarea
            id={`${uid}-description`}
            rows={3}
            value={description}
            maxLength={500}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-md rounded-xl border border-border-light p-md">
          <div className="flex items-center gap-sm" data-testid="edit-tracker-preview">
            <TrackerIconTile
              icon={icon}
              hue={hue}
              size="md"
              data-testid="edit-tracker-preview-icon"
            />
            <div className="flex min-w-0 flex-col">
              <span className="truncate type-body-md-strong text-foreground">
                {trimmedName || t('trackers.editTrackerSheet.untitledTracker')}
              </span>
              <span className="type-caption text-muted-foreground">
                {t('trackers.editTrackerSheet.preview')}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-xs">
            <div className="flex items-center gap-xs">
              <span id={colorLabelId} className="type-label-md text-foreground">
                {t('trackers.editTrackerSheet.color')}
              </span>
              <span className="type-label-md text-muted-foreground">
                {t(TRACKER_HUE_LABEL_KEYS[hue])}
              </span>
            </div>
            <div role="group" aria-labelledby={colorLabelId} className="flex flex-wrap gap-xs">
              {TRACKER_HUES.map((option) => {
                const selected = option === hue;
                const label = t(TRACKER_HUE_LABEL_KEYS[option]);
                const swatch = TRACKER_HUE_CLASSES[option];
                return (
                  <Tooltip key={option}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={label}
                        aria-pressed={selected}
                        onClick={() => setHue(option)}
                        className={`flex size-8 items-center justify-center rounded-full border-2 bg-card p-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                          selected ? swatch.border : 'border-transparent'
                        }`}
                      >
                        <span
                          className={`flex size-6 items-center justify-center rounded-full ring-1 ring-inset ${swatch.tile} ${swatch.foreground} ${swatch.edge}`}
                        >
                          {selected ? (
                            <CheckIcon className="size-3.5" strokeWidth={2.5} aria-hidden />
                          ) : null}
                        </span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{label}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-xs">
            <span id={iconLabelId} className="type-label-sm text-muted-foreground">
              {t('trackers.editTrackerSheet.icon')}
            </span>
            <div
              role="group"
              aria-labelledby={iconLabelId}
              className="grid grid-cols-[repeat(auto-fill,minmax(34px,1fr))] gap-3xs"
            >
              {TRACKER_ICONS.map((option) => {
                const selected = option === icon;
                const label = t(TRACKER_ICON_LABEL_KEYS[option]);
                const Icon = TRACKER_ICON_COMPONENTS[option];
                return (
                  <Tooltip key={option}>
                    <TooltipTrigger asChild>
                      <Toggle
                        size="sm"
                        aria-label={label}
                        pressed={selected}
                        onPressedChange={() => setIcon(option)}
                        // The tooltip trigger would otherwise overwrite the toggle's own state.
                        data-state={selected ? 'on' : 'off'}
                        className={`text-muted-foreground ${colours.pressed}`}
                      >
                        <Icon aria-hidden />
                      </Toggle>
                    </TooltipTrigger>
                    <TooltipContent>{label}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <SheetFooter className="flex-row items-center justify-end gap-xs p-0 px-xl">
        <Button variant="outline" size="lg" onClick={onClose} disabled={updateTracker.isPending}>
          {t('trackers.editTrackerSheet.cancel')}
        </Button>
        <Button size="lg" onClick={save} disabled={!trimmedName || updateTracker.isPending}>
          {updateTracker.isPending && <Spinner />}
          {t('trackers.editTrackerSheet.saveChanges')}
        </Button>
      </SheetFooter>
    </TooltipProvider>
  );
}

/**
 * Edits a tracker's name, description, icon and colour in one request. Opened from the hub
 * card's menu and the board header's; who may open it is the caller's call.
 */
export function EditTrackerSheet({
  tracker,
  open,
  onOpenChange,
  returnFocusTo,
}: {
  tracker: Tracker | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Where focus lands on close. Both entry points are menu items, and the item that had focus
   * when the sheet opened is gone by the time it closes, so without this focus falls to the page.
   */
  returnFocusTo?: RefObject<HTMLElement | null>;
}) {
  return (
    <Sheet open={open && tracker !== undefined} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        aria-describedby={undefined}
        // `gap-sm`, not the `md` it reads as: the body adds `2xs` of ring room above and below.
        className="w-[30rem] max-w-[92vw] gap-sm rounded-l-lg px-0 py-xl sm:max-w-[92vw]"
        onPointerDownOutside={guardNotistackDismissal}
        onCloseAutoFocus={(event) => {
          if (!returnFocusTo?.current) return;
          event.preventDefault();
          returnFocusTo.current.focus();
        }}
      >
        {tracker ? (
          <EditTrackerForm key={tracker.id} tracker={tracker} onClose={() => onOpenChange(false)} />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
